import { useMutation, useQueryClient } from '@tanstack/react-query';
import { listPropertyContactsHousesHouseIdContactsGetQueryKey } from '@/client/@tanstack/react-query.gen';
import { ProfessionalContacts, type ContactWithCategory } from '@/client';
import { toaster } from '@/components/ui/toaster';
import { sharingQueryKey } from '@/hooks/sharing/useSharingList';

/**
 * Adds a new contact to a property.
 * Handles backend deduplication and validation, showing appropriate
 * success or error messages to the user.
 */
export const useAddContact = (houseId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: ContactWithCategory) =>
			ProfessionalContacts.addContactToPropertyHousesHouseIdContactsPost({
				path: { house_id: houseId },
				body: data,
				throwOnError: true,
			}).then(({ data: response }) => response),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: listPropertyContactsHousesHouseIdContactsGetQueryKey({
					path: { house_id: houseId },
				}),
			});
			void queryClient.invalidateQueries({ queryKey: sharingQueryKey(houseId), refetchType: 'all' });
		},
		onError: (error) => {
			if ('status' in error && error.status === 409) {
				toaster.create({
					title: 'Contact already exists',
					description: 'This contact is already added to this category',
					type: 'error',
				});
			} else {
				toaster.create({
					title: 'Failed to add contact',
					description: error.message,
					type: 'error',
				});
			}
		},
	});
};
